// ============================================================
// FII FIT — Specializări pentru harta de specialiști
// ============================================================
// Sursa unică de adevăr pentru tipurile de specialiști afișați
// în UI. Cheile coincid cu enum-ul `SpecialtyType` din Prisma.
//
// În FII FIT (FITNESS) afișăm doar specializările din `FITNESS_*`.
// Specializările medicale (`MEDICAL_*`) sunt păstrate pentru
// retro-compatibilitate cu modul CHRONIC și sunt afișate doar când
// `isChronicEnabled()` returnează `true`.
// ============================================================

// ─── DOCTORI MEDICALI (CHRONIC ONLY) ────────────────────────
export const MEDICAL_DOCTOR_TYPES: Record<string, string> = {
  FAMILY_DOCTOR:         'Medic de familie',
  INTERNAL_MEDICINE:     'Medicină internă',
  RHEUMATOLOGIST:        'Reumatolog',
  IMMUNOLOGIST:          'Imunolog',
  ALLERGOLOGIST:         'Alergolog',
  NEUROLOGIST:           'Neurolog',
  CARDIOLOGIST:          'Cardiolog',
  PULMONOLOGIST:         'Pneumolog',
  GASTROENTEROLOGIST:    'Gastroenterolog',
  ENDOCRINOLOGIST:       'Endocrinolog',
  DIABETOLOGIST:         'Diabetolog',
  NEPHROLOGIST:          'Nefrolog',
  UROLOGIST:             'Urolog',
  GYNECOLOGIST:          'Ginecolog',
  DERMATOLOGIST:         'Dermatolog',
  HEMATOLOGIST:          'Hematolog',
  PSYCHIATRIST:          'Psihiatru',
  ENT_DOCTOR:            'ORL',
  OPHTHALMOLOGIST:       'Oftalmolog',
  ONCOLOGIST:            'Oncolog',
  GENETICIST:            'Medic genetician',
  PEDIATRICIAN:          'Pediatru',
  GERIATRICIAN:          'Geriatru',
  ORTHOPEDIST:           'Ortoped',
  REHABILITATION_DOCTOR: 'Medic recuperare medicală',
};

// ─── SPECIALIȘTI MEDICALI (CHRONIC ONLY) ────────────────────
export const MEDICAL_SPECIALIST_TYPES: Record<string, string> = {
  PHYSIOTHERAPIST:        'Kinetoterapeut',
  PHYSICAL_THERAPIST:     'Fizioterapeut',
  OCCUPATIONAL_THERAPIST: 'Ergoterapeut',
  PSYCHOLOGIST:           'Psiholog',
  PSYCHOTHERAPIST:        'Psihoterapeut',
  NUTRITIONIST:           'Nutriționist',
  DIETITIAN:              'Dietetician',
  SPEECH_THERAPIST:       'Logoped',
  MASSAGE_THERAPIST:      'Maseur medical',
  SOCIAL_WORKER:          'Asistent social',
  GENETIC_COUNSELOR:      'Consilier genetic',
  HOME_CAREGIVER:         'Îngrijitor la domiciliu',
};

// ─── SPECIALIȘTI FITNESS (FII FIT) ──────────────────────────
export const FITNESS_SPECIALIST_TYPES: Record<string, string> = {
  PERSONAL_TRAINER:           'Antrenor personal',
  STRENGTH_COACH:             'Antrenor forță & culturism',
  CROSSFIT_COACH:             'Antrenor CrossFit',
  GROUP_FITNESS_INSTRUCTOR:   'Instructor fitness de grup',
  YOGA_INSTRUCTOR:            'Instructor yoga',
  PILATES_INSTRUCTOR:         'Instructor pilates',
  RUNNING_COACH:              'Coach alergare & maraton',
  SPORTS_NUTRITIONIST:        'Nutriționist sportiv',
  SPORTS_DIETITIAN:           'Dietetician sportiv',
  SPORTS_RECOVERY_SPECIALIST: 'Specialist recuperare sportivă',
  SPORTS_MASSAGE_THERAPIST:   'Maseur sportiv',
  WELLNESS_COACH:             'Coach wellness & lifestyle',
};

// ─── Map combinat (pentru lookup de label din BD) ───────────
export const ALL_SPECIALTY_LABELS: Record<string, string> = {
  ...MEDICAL_DOCTOR_TYPES,
  ...MEDICAL_SPECIALIST_TYPES,
  ...FITNESS_SPECIALIST_TYPES,
};

// ─── Helper: cărei mari categorii aparține un tip ───────────
export type SpecialtyCategory = 'DOCTOR' | 'SPECIALIST' | 'FITNESS';

export const getCategoryForType = (type: string): SpecialtyCategory | null => {
  if (type in MEDICAL_DOCTOR_TYPES) return 'DOCTOR';
  if (type in MEDICAL_SPECIALIST_TYPES) return 'SPECIALIST';
  if (type in FITNESS_SPECIALIST_TYPES) return 'FITNESS';
  return null;
};

// ─── Helper: dezambiguare label sigură ──────────────────────
export const getSpecialtyLabel = (type: string): string =>
  ALL_SPECIALTY_LABELS[type] || type;

// ─── Helper: tipurile disponibile în modul curent ───────────
// În FII FIT (FITNESS) returnează doar specializările fitness.
// În modul CHRONIC returnează totul.
export const getAvailableSpecialtyTypes = (
  chronicEnabled: boolean
): Record<string, string> =>
  chronicEnabled
    ? { ...MEDICAL_DOCTOR_TYPES, ...MEDICAL_SPECIALIST_TYPES, ...FITNESS_SPECIALIST_TYPES }
    : { ...FITNESS_SPECIALIST_TYPES };
